import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Input, { BareInput } from "components/DesignSystem/Input/Input";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import {
  CHURN_REASON_OPTIONS,
  CSM_SCORE_OPTIONS,
  OTHER_CHURN_REASON,
  SAVE_PROBABILITY_OPTIONS,
  YES_NO_OPTIONS,
} from "constants/churnDetails";
import { Field, FieldProps } from "formik";
import { TypedFormikProps } from "hooks/useTypedFormikContext";
import { filterOptionsByValues } from "util/filterOptionsByValues";
import { findOptionByValue } from "util/findOptionByValue";
import { FilingsList } from "./FilingsList";
import { ChurnDetailsFormValues } from "./useChurnDetailsFormData";

type ChurnDetailsFormProps = {
  values: ChurnDetailsFormValues;
  setFieldValue: TypedFormikProps<ChurnDetailsFormValues>["setFieldValue"];
  initialValues: ChurnDetailsFormValues;
  productOptions: OptionData[];
  isReviewMode: boolean;
  isSubmitting: boolean;
  onRequestAmClick: () => void;
};

// Referential identity matters here -- Combobox treats a new `components` object as a
// reason to re-render its internal react-select instance on every parent render.
const HIDE_MULTI_VALUE_REMOVE = { MultiValueRemove: () => null };

const SectionHeading = ({ children }: { children: string }) => (
  <h3 className="t-col-span-2 t-mb-0 t-mt-2 t-text-subtitle t-text-text-100 first:t-mt-0">
    {children}
  </h3>
);

const OrDivider = () => (
  <div className="t-flex t-w-full t-items-center t-gap-4">
    <span className="t-h-px t-flex-1 t-bg-neutral-10" />
    <span className="t-text-body-sm t-font-medium t-text-text-30">or</span>
    <span className="t-h-px t-flex-1 t-bg-neutral-10" />
  </div>
);

const YesNoField = ({
  name,
  label,
  value,
  onValueChange,
  disabled = false,
  required = false,
}: {
  name: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
}) => (
  <div>
    <Label required={required}>{label}</Label>
    <Radio.Root
      name={name}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <Radio.Content>
        <div className="t-flex t-items-center t-gap-2">
          {YES_NO_OPTIONS.map((option) => (
            <Radio.Item
              className="!t-w-auto"
              disabled={disabled}
              value={option.value}
              id={`${name}-${option.value}`}
              key={option.value}
            >
              {option.label}
            </Radio.Item>
          ))}
        </div>
      </Radio.Content>
    </Radio.Root>
  </div>
);

export const ChurnDetailsForm = ({
  values,
  setFieldValue,
  initialValues,
  productOptions,
  isReviewMode,
  isSubmitting,
  onRequestAmClick,
}: ChurnDetailsFormProps) => {
  const defaultProductsValues = filterOptionsByValues(
    productOptions,
    initialValues.products_subscribed
  );
  const defaultSaveProbability = findOptionByValue(
    SAVE_PROBABILITY_OPTIONS,
    values.save_probability
  );
  const defaultCsmScore = findOptionByValue(
    CSM_SCORE_OPTIONS,
    values.csm_score
  );
  const defaultChurnReason = findOptionByValue(
    CHURN_REASON_OPTIONS,
    values.churn_reason
  );

  return (
    <div className="t-grid t-grid-cols-2 t-gap-4">
      <SectionHeading>Company Details</SectionHeading>
      <Input
        withForm
        disabled
        name="customer_name"
        label="Customer Name"
        placeholder="None"
      />
      <BareInput
        disabled
        label="AM Name"
        placeholder="None"
        value={initialValues.spoc_name.join(", ")}
      />
      <Field name="joining_date">
        {({ field }: FieldProps) => (
          <DateInput
            {...field}
            disabled
            label="Joining Date"
            placeholder="None"
          />
        )}
      </Field>
      <Input
        withForm
        disabled
        name="total_revenue_contribution"
        label="Total Revenue Contribution"
        placeholder="None"
      />
      <YesNoField
        name="company_dissolved"
        label="Company Dissolved"
        value={values.company_dissolved}
        onValueChange={(value) => setFieldValue("company_dissolved", value)}
        required
      />
      <Combobox
        withForm
        isMulti
        isDisabled
        name="products_subscribed"
        label="Products Subscribed"
        options={productOptions}
        defaultValue={defaultProductsValues}
        placeholder="None"
        menuPlacement="auto"
        maxMenuHeight={220}
        components={HIDE_MULTI_VALUE_REMOVE}
      />
      <div className="t-col-span-2">
        <FilingsList lines={values.filings_status} />
      </div>

      <SectionHeading>Churn Details</SectionHeading>
      <Combobox
        withForm
        required
        name="save_probability"
        label="Save Probability"
        options={SAVE_PROBABILITY_OPTIONS}
        defaultValue={defaultSaveProbability}
        placeholder="Select"
        menuPlacement="auto"
        maxMenuHeight={220}
      />
      <Combobox
        withForm
        required
        name="csm_score"
        label="CSM's Score (likelihood to return)"
        options={CSM_SCORE_OPTIONS}
        defaultValue={defaultCsmScore}
        placeholder="Select"
        menuPlacement="auto"
        maxMenuHeight={220}
      />
      <YesNoField
        name="escalations_involved"
        label="Any escalations involved?"
        value={values.escalations_involved}
        onValueChange={(value) => setFieldValue("escalations_involved", value)}
        required
      />
      <YesNoField
        name="considered_churn"
        label="Considered Churn"
        value={values.considered_churn}
        onValueChange={(value) => setFieldValue("considered_churn", value)}
        required
      />
      <div className="t-col-span-2">
        <Combobox
          withForm
          required
          name="churn_reason"
          label="Primary reason for churn"
          options={CHURN_REASON_OPTIONS}
          defaultValue={defaultChurnReason}
          placeholder="Select a reason"
          menuPlacement="auto"
          maxMenuHeight={220}
        />
      </div>
      {values.churn_reason === OTHER_CHURN_REASON && (
        <div className="t-col-span-2">
          <TextArea
            required
            name="other_reason_detail"
            label="Please specify the reason"
            placeholder="What was the specific reason for churn?"
          />
        </div>
      )}
      <YesNoField
        name="save_attempt_performed"
        label="Save attempt performed"
        value={values.save_attempt_performed}
        onValueChange={(value) =>
          setFieldValue("save_attempt_performed", value)
        }
        required
      />
      <div className="t-col-span-2">
        <TextArea
          name="other_notes"
          label="Other Notes"
          placeholder="Anything else worth noting"
        />
      </div>

      {!isReviewMode && (
        <div className="t-col-span-2 t-flex t-flex-col t-gap-4">
          <OrDivider />
          <Button
            type="button"
            block
            customType="secondary"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={onRequestAmClick}
          >
            Request AM to fill in the details
          </Button>
        </div>
      )}
    </div>
  );
};
